import {
  calculatePortfolioMetrics,
  formatCompact,
  formatMoney,
  formatPercent,
} from "@seed-factory/core";
import {
  Activity,
  AlertTriangle,
  BadgeDollarSign,
  CircleDollarSign,
  Gauge,
  Rocket,
  Sprout,
  Store,
  Target,
  TrendingDown,
  UsersRound,
  Zap,
} from "lucide-react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartCard } from "../components/ChartCard";
import { MetricCard } from "../components/MetricCard";
import { PageHeader } from "../components/PageHeader";
import { ErrorState, LoadingState } from "../components/StateViews";
import { StatusPill } from "../components/StatusPill";
import { useFilters } from "../contexts/FiltersContext";
import { buildSeries } from "../lib/analytics";
import { useEstufaData } from "../lib/queries";

function tooltipMoney(value: number) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export function OverviewPage() {
  const { filter } = useFilters();
  const query = useEstufaData();
  if (query.isLoading) return <LoadingState />;
  if (query.isError || !query.data) return <ErrorState />;
  const data = query.data;
  const metrics = calculatePortfolioMetrics({
    installations: data.installations,
    subscriptions: data.subscriptions,
    revenue: data.revenue,
    events: data.events,
    from: filter.from,
    to: filter.to,
  });
  const series = buildSeries({
    installations: data.installations,
    subscriptions: data.subscriptions,
    revenue: data.revenue,
    from: filter.from,
    to: filter.to,
    granularity: filter.granularity,
  });
  const openIncidents = data.incidents.filter((item) => item.status !== "resolved").length;
  const published = data.seeds.filter((seed) =>
    ["published", "scaling", "maintenance"].includes(seed.status),
  ).length;
  const homologation = data.seeds.filter((seed) => seed.status === "homologation").length;

  return (
    <>
      <PageHeader
        eyebrow="Portfólio vivo"
        title="Visão Geral"
        description="O que está crescendo, o que está quebrando e onde a fábrica está aprendendo."
      />
      <div className="metrics-grid">
        <MetricCard
          label="Seeds publicadas"
          value={String(published)}
          icon={Sprout}
          tone="emerald"
          hint={`${homologation} em homologação`}
        />
        <MetricCard
          label="Instalações"
          value={formatCompact(metrics.installs)}
          icon={Store}
          tone="violet"
          hint={`${metrics.activeInstallations} ativas`}
        />
        <MetricCard
          label="Ativação"
          value={formatPercent(metrics.activationRate)}
          icon={Zap}
          tone="blue"
          hint={`${metrics.activations} ativações`}
        />
        <MetricCard
          label="First Value"
          value={formatPercent(metrics.firstValueRate)}
          icon={Target}
          tone="cyan"
          hint={`${metrics.firstValues} chegaram ao valor`}
        />
        <MetricCard
          label="Clientes pagos"
          value={String(metrics.paidCustomers)}
          icon={UsersRound}
          tone="emerald"
          hint={`ARPU ${metrics.arpuCents === null ? "—" : formatMoney(metrics.arpuCents)}`}
        />
        <MetricCard
          label="MRR"
          value={formatMoney(metrics.mrrCents)}
          icon={CircleDollarSign}
          tone="violet"
          hint={`ARR ${formatMoney(metrics.arrCents)}`}
        />
        <MetricCard
          label="Receita no período"
          value={formatMoney(metrics.revenueCents)}
          icon={BadgeDollarSign}
          tone="blue"
        />
        <MetricCard
          label="Churn"
          value={formatPercent(metrics.logoChurnRate)}
          icon={TrendingDown}
          tone="coral"
          hint={`${metrics.churnedCustomers} cancelamentos`}
        />
        <MetricCard
          label="TTFV mediano"
          value={
            metrics.medianTtfvMinutes === null
              ? "—"
              : `${Math.round(metrics.medianTtfvMinutes)} min`
          }
          icon={Rocket}
          tone="amber"
          hint="install → first value"
        />
        <MetricCard
          label="Incidentes abertos"
          value={String(openIncidents)}
          icon={AlertTriangle}
          tone={openIncidents ? "coral" : "emerald"}
        />
        <MetricCard
          label="Health Score"
          value="Dados insuficientes"
          icon={Gauge}
          tone="amber"
          hint="Só aparece com alvos configurados"
        />
        <MetricCard
          label="Eventos observados"
          value={formatCompact(data.events.length)}
          icon={Activity}
          tone="cyan"
          hint="no recorte atual"
        />
      </div>

      <div className="dashboard-grid">
        <ChartCard
          title="MRR ao longo do tempo"
          subtitle="Receita mensal recorrente ativa no fim de cada bucket"
          className="chart-card--wide"
        >
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={series} margin={{ left: 0, right: 12, top: 10, bottom: 0 }}>
              <defs>
                <linearGradient id="mrrFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--violet)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="var(--violet)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
              <Tooltip formatter={(value) => tooltipMoney(Number(value))} />
              <Area
                type="monotone"
                dataKey="mrr"
                name="MRR"
                stroke="var(--violet)"
                strokeWidth={3}
                fill="url(#mrrFill)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard
          title="Instalações x ativações"
          subtitle="Aquisição não vale nada se ninguém chegar ao uso"
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} />
              <Tooltip />
              <Legend />
              <Bar
                dataKey="installs"
                name="Instalações"
                fill="var(--violet)"
                radius={[6, 6, 0, 0]}
              />
              <Bar
                dataKey="activations"
                name="Ativações"
                fill="var(--emerald)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="First Value" subtitle="Quantas ativações realmente viram valor">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="firstValue"
                name="First Value"
                stroke="var(--cyan)"
                strokeWidth={3}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard
          title="Receita reconhecida"
          subtitle="Valor efetivamente registrado no período"
          className="chart-card--wide"
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={series}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} />
              <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => `R$${v}`} />
              <Tooltip formatter={(value) => tooltipMoney(Number(value))} />
              <Bar dataKey="revenue" name="Receita" fill="var(--blue)" radius={[7, 7, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <section className="table-card">
        <div className="card-heading">
          <div>
            <h3>Seeds</h3>
            <p>Uma visão curta do portfólio. Seeds mortas permanecem aqui.</p>
          </div>
        </div>
        <div className="responsive-table">
          <table>
            <thead>
              <tr>
                <th>Seed</th>
                <th>Status</th>
                <th>Plataformas</th>
                <th>Última release</th>
                <th>Incidentes</th>
              </tr>
            </thead>
            <tbody>
              {data.seeds.map((seed) => {
                const deps = data.deployments.filter((item) => item.seedId === seed.id);
                const release = data.releases.find((item) =>
                  deps.some((dep) => dep.id === item.deploymentId),
                );
                const incidents = data.incidents.filter(
                  (item) =>
                    item.deploymentId &&
                    deps.some((dep) => dep.id === item.deploymentId) &&
                    item.status !== "resolved",
                ).length;
                return (
                  <tr key={seed.id}>
                    <td>
                      <strong>{seed.name}</strong>
                      <span className="table-subtitle">{seed.seedKey}</span>
                    </td>
                    <td>
                      <StatusPill value={seed.status} />
                    </td>
                    <td>{deps.map((dep) => dep.platformSlug).join(", ") || "—"}</td>
                    <td>{release?.version ?? "—"}</td>
                    <td>{incidents}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
